import { t9n } from 't9n/t9n'

import { RegisterPayload } from 'models/endpoints-payload'
import { RegisterResponse } from 'models/endpoints-respone'

import { getCurrentLanguage } from 'utils/current-language'
import { pushDataLayer } from 'utils/data-layer-push'
import { getGenericErrorMessage, isApiError } from 'utils/error-message'

import { emailValidator, emptyValidator, passwordValidator, checkedValidator } from 'services/validators-functions'
import { registerUser } from 'services/register-user'
import { createUrlWithParams } from 'utils/urls'

import { bindPasswordInput } from './password-input'

export type AllowedFunctionalCookiesForPanel = 'language' | 'registrationRef'

type AllowedFunctionalCookiesForPanelArray = {
  [key in AllowedFunctionalCookiesForPanel]?: string
}

declare let USER_BASE_URL_IK: string

const $submitFormButton = document.querySelector<HTMLButtonElement>('[data-submit-button]')
const $emailInputElement = document.querySelector<HTMLInputElement>("input[name='email']")
const $passwordInputElement = document.querySelector<HTMLInputElement>("input[name='password']")
const $serviceTermsInputElement = document.querySelector<HTMLInputElement>("input[name='reg-consent']")
const $promoTermsInputElement = document.querySelector<HTMLInputElement>("input[name='prom-consent']")
const $marketingConsentInputElement = document.querySelector<HTMLInputElement>("input[name='marketing-consent']")
const $promocodeInputElement = document.querySelector<HTMLInputElement>("input[name='promo-code']")
const $allErrorSpans = document.querySelectorAll<HTMLElement>('[data-error]')
const $allErrorCheckboxSpans = document.querySelectorAll<HTMLElement>('[data-checkbox-error]')

const getEmailFromForm = (): string => {
  return $emailInputElement.value.trim()
}

const getPasswordFromForm = (): string => {
  return $passwordInputElement.value.trim()
}

const getServiceTermConsentFromForm = (): boolean => {
  return $serviceTermsInputElement.checked
}

const getPromotionTermConsentFromForm = (): boolean => {
  return $promoTermsInputElement.checked
}

const getMarketingConsentFromForm = (): boolean => {
  return $marketingConsentInputElement.checked
}

const getPromocodeValueFromForm = (): string => {
  return $promocodeInputElement.value.trim()
}

const getErrorContent = (errorID: string): string => {
  const currentLanguage = getCurrentLanguage()
  const promocodeExpiredUrl = ' <a href="internetowykantor.pl/?ref=land-promo-expired">internetowykantor.pl</a>'
  switch (errorID) {
    case 'email-mandatory':
    case 'password-mandatory':
      return t9n('landingForm.notEmptyField', undefined, currentLanguage)
    case 'email-invalid':
      return t9n('landingForm.incorrectEmail', undefined, currentLanguage)
    case 'password-too-short':
      return t9n('landingForm.toShortPassword', undefined, currentLanguage)
    case 'promocode-invalid':
      return t9n('landingForm.invalidPromocode', undefined, currentLanguage) + promocodeExpiredUrl
    case 'email-already-exists':
      return t9n('landingForm.emailAlreadyExists', undefined, currentLanguage)
    case 'general-invalid':
      return t9n('landingForm.generalInvalid', undefined, currentLanguage)
    default:
      return t9n('landingForm.requiredField', undefined, currentLanguage)
  }
}

const showCheckboxError = (errorID: string): void => {
  const $errorElement = document.querySelector<HTMLElement>(`[data-checkbox-error='${errorID}']`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.innerHTML = getErrorContent(errorID)
  }
}

const showError = ($element: HTMLInputElement, errorID: string): void => {
  if ($element) {
    $element.classList.add('is-error')
  }

  const errorDataSelectorName = errorID.split('-')[0]

  const $errorElement = document.querySelector<HTMLElement>(`[data-error='${errorDataSelectorName}']`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.innerHTML = getErrorContent(errorID)
  }
}

const resetAllErrors = () => {
  $emailInputElement.classList.remove('is-error')
  $passwordInputElement.classList.remove('is-error')

  for (const $singleErrorSpan of $allErrorSpans) {
    $singleErrorSpan.hidden = true
  }

  for (const $singleErrorCheckboxSpan of $allErrorCheckboxSpans) {
    $singleErrorCheckboxSpan.hidden = true
  }
}

const checkEmailValidation = (): boolean => {
  if (emptyValidator(getEmailFromForm())) {
    showError($emailInputElement, 'email-mandatory')
    return false
  }

  if (emailValidator(getEmailFromForm())) {
    showError($emailInputElement, 'email-invalid')
    return false
  }

  return true
}

const checkPasswordValidation = (): boolean => {
  if (emptyValidator(getPasswordFromForm())) {
    showError($passwordInputElement, 'password-mandatory')
    return false
  }

  if (passwordValidator(getPasswordFromForm())) {
    showError($passwordInputElement, 'password-too-short')
    return false
  }

  return true
}

const checkTermsOfServiceValidation = (): boolean => {
  if (checkedValidator(getServiceTermConsentFromForm())) {
    showCheckboxError('reg-consent-mandatory')
    return false
  }

  return true
}

const checkPromotionTermsValidation = (): boolean => {
  if (checkedValidator(getPromotionTermConsentFromForm())) {
    showCheckboxError('prom-consent-mandatory')
    return false
  }

  return true
}

// Function fetches data from form and create payload object.
export const getFormData = (): Partial<RegisterPayload> => {
  return {
    email: getEmailFromForm(),
    password: getPasswordFromForm(),
    promocode: getPromocodeValueFromForm(),
    terms_and_conditions_accepted: getServiceTermConsentFromForm(),
    promotion_program_rules_accepted: $promoTermsInputElement ? getPromotionTermConsentFromForm() : undefined,
    marketing_email_actions_accepted: getMarketingConsentFromForm() || undefined,
    region: 'PL', // deprecated stuff - long history of WT
    language: getCurrentLanguage(),
  }
}

const isFormComplete = (): boolean => {
  return [
    checkEmailValidation(),
    checkPasswordValidation(),
    checkTermsOfServiceValidation(),
    $promoTermsInputElement ? checkPromotionTermsValidation() : true,
  ].every((v) => v)
}

const handleFormSubmit = async (): Promise<void> => {
  resetAllErrors()
  const payload = getFormData() as RegisterPayload

  if (!isFormComplete()) {
    console.warn('Incomplete or invalid form data')
    return
  }

  try {
    const response = await registerUser(payload)
    handleSuccessfulRegistration(payload, response)
  } catch (error: unknown) {
    handleFailedRegistration(error)
  }
}

export const createConfirmEmailUrl = (domain: string, params: AllowedFunctionalCookiesForPanelArray): string => {
  const redirect = `${domain}/#/confirm-email`
  const createdUrl = createUrlWithParams(params, new URL(redirect))

  return createdUrl
}

const handleSuccessfulRegistration = (payload: RegisterPayload, response: RegisterResponse): void => {
  const language = payload.language || getCurrentLanguage()
  const registrationRef = response.ref || ''

  pushDataLayer('form_submitted', { form_name: 'Rejstracja z landingu', form_url: window.location.href })

  window.location.href = createConfirmEmailUrl(USER_BASE_URL_IK, { language, registrationRef })
}

export const handleFailedRegistration = (error: unknown): void => {
  if (isApiError(error)) {
    const { data } = error.response

    if (data.error) {
      switch (data.error) {
        case 'email-already-exists':
          showError($emailInputElement, 'email-already-exists')
          return
        case 'promocode-invalid':
          showError(null, 'promocode-invalid')
      }
    }

    const genericMessage = getGenericErrorMessage(error)
    console.error('Registration error:', genericMessage)
    showError(null, 'general-invalid')
  }
}

function paramInjector(fn, ...args) {
  return function (...otherArgs) {
    return fn.apply(this, [...args, ...otherArgs])
  }
}

export function handleForm(): void {
  $submitFormButton.addEventListener('click', paramInjector(handleFormSubmit))
  bindPasswordInput($passwordInputElement)
}
